"use client"

import type React from "react"
import { useState, useEffect, useRef } from "react"
import { X } from "lucide-react"
import { stateNameToAbbrev, stateAbbrevToName, stateAbbreviations } from "../utils/stateMappings"
import { countryNameToAbbrev, countryAbbrevToName, countryAbbreviations } from "../utils/countryMappings"

interface TerritoryInputProps {
  value: string[]
  onChange: (value: string[]) => void
  mapMode: "us" | "world"
  className?: string
  onClick?: (e: React.MouseEvent) => void
}

const TerritoryInput: React.FC<TerritoryInputProps> = ({ value, onChange, mapMode, className = "", onClick }) => {
  const [inputValue, setInputValue] = useState("")
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  const territories = value || []

  // Get all valid options for autocomplete
  const getMappings = () => {
    if (mapMode === "us") {
      const stateNames = Object.keys(stateAbbreviations)
      const stateAbbrevs = Object.values(stateAbbreviations)
      return [...stateNames, ...stateAbbrevs]
    } else {
      const countryNames = Object.keys(countryAbbreviations)
      const countryAbbrevs = Object.values(countryAbbreviations)
      return [...countryNames, ...countryAbbrevs]
    }
  }

  // Get the abbreviation for a name or return the input if it's already an abbreviation
  const getAbbreviation = (input: string): string => {
    const trimmed = input.trim()

    if (mapMode === "us") {
      // Check if input is already a valid US state abbreviation
      const upperInput = trimmed.toUpperCase()
      if (Object.values(stateAbbreviations).includes(upperInput as any)) {
        return upperInput
      }
      // Try to convert from state name to abbreviation
      return stateNameToAbbrev(trimmed)
    } else {
      // Check if input is already a valid country abbreviation
      const upperInput = trimmed.toUpperCase()
      if (Object.values(countryAbbreviations).includes(upperInput)) {
        return upperInput
      }
      // Try to convert from country name to abbreviation
      return countryNameToAbbrev(trimmed)
    }
  }

  // Get the full name for an abbreviation
  const getName = (abbrev: string) => {
    return mapMode === "us" ? stateAbbrevToName(abbrev) : countryAbbrevToName(abbrev)
  }

  // Handle clicks outside the component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setInputValue(val)
    setError(null)
    if (val.length > 0) {
      const allOptions = getMappings()
      const filtered = allOptions.filter((option) => option.toLowerCase().includes(val.toLowerCase()))
      setSuggestions(filtered.slice(0, 10))
      setShowSuggestions(true)
    } else {
      setSuggestions([])
      setShowSuggestions(false)
    }
  }

  // Handle adding a new territory
  const handleAdd = () => {
    const trimmed = inputValue.trim()
    if (!trimmed) return

    // Get the abbreviation
    const abbrev = getAbbreviation(trimmed)

    // Validate the abbreviation
    if (mapMode === "us") {
      if (!Object.values(stateAbbreviations).includes(abbrev as any)) {
        setError("Invalid state. Please check your input.")
        return
      }
    } else {
      if (!Object.values(countryAbbreviations).includes(abbrev)) {
        setError("Invalid country. Please check your input.")
        return
      }
    }

    // Add the territory if it's not already included
    if (!territories.includes(abbrev)) {
      onChange([...territories, abbrev].sort())
    }

    // Reset the input
    setInputValue("")
    setSuggestions([])
    setShowSuggestions(false)
    setError(null)
  }

  // Handle keyboard events
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleAdd()
    }
  }

  // Handle clicking on a suggestion
  const handleSuggestionClick = (suggestion: string) => {
    const abbrev = getAbbreviation(suggestion)
    if (!territories.includes(abbrev)) {
      onChange([...territories, abbrev].sort())
    }
    setInputValue("")
    setSuggestions([])
    setShowSuggestions(false)
  }

  // Handle removing a territory
  const handleRemoveTerritory = (territory: string) => {
    onChange(territories.filter((t) => t !== territory))
  }

  return (
    <div className={`relative territory-input-container ${className}`} onClick={onClick}>
      <div className="flex flex-wrap gap-2 p-2 border rounded bg-white min-h-[42px]">
        {territories.map((territory) => (
          <div
            key={territory}
            className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
          >
            <span>
              {getName(territory)} ({territory})
            </span>
            <button onClick={() => handleRemoveTerritory(territory)} className="hover:text-blue-950">
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <div className="flex-1 min-w-[150px]">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className="w-full outline-none border-none bg-transparent"
            placeholder={`Type ${mapMode === "us" ? "state" : "country"} name...`}
          />
          {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
        </div>
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 mt-1 w-full max-h-60 overflow-auto bg-white border rounded-md shadow-lg"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              className="w-full px-4 py-2 text-left hover:bg-blue-50 text-sm"
            >
              {suggestion} ({getAbbreviation(suggestion)})
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default TerritoryInput
